/* eslint-disable import/no-unresolved */
import { debounce } from 'lib-utils'
/* eslint-disable import/no-unresolved */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.cachedResults = {}
    this.searchTerm = ''
    this.predictiveSearchResults = this.querySelector(
      '.predictive-search-results'
    )
    this.focusableElements =
      'button, a, input:not([type="hidden"]), textarea, select, details, summary, [tabindex]:not([tabindex="-1"])'
  }

  connectedCallback() {
    this.predictiveSearchResults = this.querySelector(
      '.predictive-search-results'
    )
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search')
    this.searchInputs = document.querySelectorAll('[data-search-input]')
    this.searchInputs.forEach((inp) => {
      inp.addEventListener(
        'input',
        debounce((event) => {
          this.onInputChange(event)
        }, 300).bind(this, inp)
      )
    })
    this.searchBtns = document.querySelectorAll('[data-search-toggle]')
    this.openSearchModalInputs = document.querySelectorAll(
      '[data-search-open-input]'
    )
    this.searchModal = document.querySelector('[data-search-target]')

    this.searchBtns.forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.toggle(btn)
      })
    )

    this.addEventListener('click', (e) => {
      e.stopPropagation()
    })

    this.openSearchModalInputs.forEach((inp) => {
      inp.addEventListener('input', (e) => {
        if (e.currentTarget.value) {
          this.open(e)
          this.syncInputs(e)
        }
      })
    })

    document.body.addEventListener('click', () => {
      this.close()
    })

    this.closeSearchModalBtns = document.querySelectorAll('[data-search-close]')

    this.closeSearchModalBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.close()
      })
    })
    document.addEventListener('keydown', this.handleEscapeKey.bind(this))
  }

  disconnectedCallback() {
    this.searchInputs = document.querySelectorAll('[data-search-input]')
    this.searchInputs.forEach((inp) => {
      inp.removeEventListener(
        'input',
        debounce((event) => {
          this.onInputChange(event)
        }, 300).bind(this, inp)
      )
    })
    document.removeEventListener('keydown', this.handleEscapeKey.bind(this))
  }

  open(e) {
    if (e.currentTarget.dataset.searchOpenInput !== this.dataset.search) return
    this.classList.add('active')
    document.body.style.overflow = 'hidden'
    this.searchInputs.forEach((inp) => inp.setAttribute('aria-expanded', true))
    this.addTrapFocus(this.querySelector('[data-search-input]'))
    if (this.closest('.drawer'))
      this.closest('.drawer').style.overflow = 'hidden'
  }

  close() {
    if (!this.classList.contains('active')) return
    this.classList.remove('active')
    this.searchInputs.forEach((inp) => inp.setAttribute('aria-expanded', false))
    if (!this.dataset.disableLock) {
      document.body.style.removeProperty('overflow')
    }
    if (this.closest('.drawer'))
      this.closest('.drawer').style.removeProperty('overflow')
    this.removeTrapFocus()
  }

  syncInputs(e) {
    this.searchInputs.forEach((inp) => {
      inp.value = e.currentTarget.value
    })
  }

  onInputChange(inp) {
    const newSearchTerm = inp.value.trim()

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.predictiveSearchResults.innerHTML = ''
    }

    this.searchTerm = newSearchTerm

    if (!this.searchTerm.length) {
      this.predictiveSearchResults.innerHTML = ''
      return
    }

    this.getSearchResults(this.searchTerm)
  }

  toggle(btn) {
    if (btn.dataset.searchToggle !== this.dataset.search) return
    if (this.classList.contains('active')) {
      this.classList.remove('active')
      if (!this.dataset.disableLock) {
        document.body.style.removeProperty('overflow')
      }
      btn.setAttribute('aria-expanded', false)
      this.removeTrapFocus()
    } else {
      this.classList.add('active')
      document.body.style.overflow = 'hidden'
      btn.setAttribute('aria-expanded', true)
      this.addTrapFocus(this.querySelector('[data-search-input]'))
    }
  }

  addTrapFocus(focusEl) {
    this.previousActiveElement = document.activeElement

    this.focusableEls = Array.from(
      this.querySelectorAll(this.focusableElements)
    )
    this.firstFocusableEl = this.focusableEls[0]
    this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1]
    this.focusableEls.forEach((el) => {
      el.addEventListener('keydown', this.handleFocusTrap.bind(this))
    })

    focusEl?.focus({
      preventScroll: true,
    })
  }

  removeTrapFocus() {
    this.focusableEls?.forEach((el) => {
      el.removeEventListener('keydown', this.handleFocusTrap)
    })

    this.previousActiveElement?.focus({
      preventScroll: true,
    })
  }

  handleFocusTrap(e) {
    if (e.key !== 'Tab') return
    const firstFocusableEl = this.firstFocusableEl
    const lastFocusableEl = this.lastFocusableEl
    const isTabForward = !e.shiftKey
    const isTabBackward = e.shiftKey

    const isFirstElement = document.activeElement === firstFocusableEl
    const isLastElement = document.activeElement === lastFocusableEl

    if ((isTabBackward && isFirstElement) || (isTabForward && isLastElement)) {
      e.preventDefault()
      isTabBackward ? lastFocusableEl.focus() : firstFocusableEl.focus()
    }
  }

  handleEscapeKey(e) {
    if (!this.classList.contains('active')) return
    if (e.key !== 'Escape') return

    this.close()
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    fetch(
      `${this.dataset.predictiveSearchUrl}?${new URLSearchParams({
        q: searchTerm,
        section_id: 'predictive-search',
        'resources[type]': 'query,product,collection,article,page',
        'resources[fields]':
          'author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor',
        'resources[limit_scope]': 'each',
      }).toString()}`
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          this.close()
          throw error
        }
        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            // eslint-disable-next-line no-param-reassign
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup
          }
        )
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return
        }
        this.close()
        throw error
      })
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)
    this.removeTrapFocus()
    this.addTrapFocus(this.querySelector('[data-search-input]'))
  }
}

customElements.define('predictive-search', PredictiveSearch)
